<script setup lang="ts">
  // Always-visible Club member panel of the order money pane: who the
  // member is, whether the cashier is still identified (grant countdown),
  // and one-tap "Usar puntos" / "Usar saldo" with the live figures. Orders
  // without a member get the same slot as an "Identificar socio" row.
  // Only mounted by POS V2 hosts that provide ORDER_CLUB_CONTEXT_KEY.
  import { computed, getCurrentInstance, inject, ref, Ref } from 'vue';
  import { useElementSize } from '@vueuse/core';
  import { PermissionType } from '@/enums/PermissionType';
  import { IClubOrderMember } from '@/interfaces/IClubMember';
  import { IOrder } from '@/interfaces/IOrder';
  import {
    getClubMemberPanelState,
    IClubPanelMember,
  } from '@/domain/club/club-member-panel';
  import { formatCountdown } from '@/domain/club/club-verification';
  import { CLUB_PANEL_ROW_MIN } from '@/domain/pos/pos-compaction';
  import { useAuth } from '@/composition/use-auth';
  import { ORDER_CLUB_CONTEXT_KEY } from '@/composition/use-order-club-context';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { usePosOrderPayments } from '@/composition/use-pos-order-payments';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2ClubMemberEmptyRow from './V2ClubMemberEmptyRow.vue';
  import V2ClubMemberPanelTile from './V2ClubMemberPanelTile.vue';

  const order = inject<Ref<IOrder>>('order')!;
  const clubContext = inject(ORDER_CLUB_CONTEXT_KEY, null);

  const auth = useAuth();
  const restaurantStore = useRestaurantStore();
  const money = usePosMoney();
  const {
    showRedeemPointsModal,
    payWithClubBalance,
    clubAttachAvailable,
    showAttachClubModal,
  } = usePosOrderPayments(order, clubContext);

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const canAct = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  // Width-only measurement: the on-screen keyboard changes height, never
  // width, so this tier can't flip under a focused input.
  const rootRef = ref<HTMLElement | null>(null);
  const { width: panelWidth } = useElementSize(rootRef);
  const rowLayout = computed(() => panelWidth.value >= CLUB_PANEL_ROW_MIN);

  // Context figures win; until they load, fall back to the order embed
  // (points only — the wallet is unknown, so its tile shows a skeleton).
  const contextMember = computed<IClubOrderMember | null>(
    () => clubContext?.member.value ?? null
  );
  const identity = computed(() => {
    const embed = order.value.clubMember;
    const member = contextMember.value;
    return {
      name: member?.name ?? embed?.name ?? '',
      memberNumber: member?.memberNumber ?? embed?.memberNumber ?? null,
    };
  });
  const panelMember = computed<IClubPanelMember>(() => ({
    pointsBalance:
      contextMember.value?.pointsBalance ??
      order.value.clubMember?.pointsBalance ??
      0,
    cashTotal: contextMember.value?.cashTotal ?? null,
  }));
  const balanceLoading = computed(
    () => panelMember.value.cashTotal == null && !!clubContext?.loading.value
  );

  const state = computed(() =>
    getClubMemberPanelState({
      program: restaurantStore.restaurant?.club,
      branchId: restaurantStore.selectedBranch?.id,
      member: panelMember.value,
      order: order.value,
    })
  );

  const verified = computed(() => clubContext?.verified.value ?? false);
  const countdown = computed(() =>
    formatCountdown(clubContext?.secondsLeft.value ?? 0)
  );

  const pointsValueText = computed(() =>
    i18nT('posV2.clubPointsWorth', {
      points: panelMember.value.pointsBalance,
      value: money.formatComputedMoney(state.value.points.value),
    })
  );
  // Inside the action the money comes first: it is what gets paid.
  const pointsActionFigure = computed(
    () =>
      `${money.formatComputedMoney(state.value.points.value)} · ${
        panelMember.value.pointsBalance
      } ${i18nT('posV2.clubPointsSuffix')}`
  );

  const pointsCaption = computed<string | null>(() => {
    switch (state.value.points.reason) {
      case 'belowMin':
        return i18nT('posV2.clubReasonBelowMin', {
          points: state.value.points.missingPoints,
        });
      case 'noPoints':
        return i18nT('posV2.clubReasonNoPoints');
      case 'noAmount':
        return i18nT('posV2.clubReasonNoAmount');
      case 'amountTooLow':
        return i18nT('posV2.clubReasonAmountTooLow');
      default:
        return null;
    }
  });

  const balanceValueText = computed(() =>
    money.formatComputedMoney(panelMember.value.cashTotal ?? 0)
  );

  const balanceCaption = computed<string | null>(() => {
    switch (state.value.balance.reason) {
      case 'noBalance':
        return i18nT('posV2.clubReasonNoBalance');
      case 'noAmount':
        return i18nT('posV2.clubReasonNoAmount');
      default:
        return null;
    }
  });

  // Tiles for a resource the program doesn't offer at all stay hidden: a
  // permanently neutral tile would only add noise. The wallet tile also
  // hides when its figure could not be loaded (context request failed).
  const showPointsTile = computed(
    () => state.value.points.reason !== 'disabled'
  );
  const showBalanceTile = computed(
    () =>
      state.value.balance.reason !== 'disabled' &&
      (panelMember.value.cashTotal != null || balanceLoading.value)
  );

  // Member-less orders offer the link row only to cashiers who may link
  // (server: OrdersManage); viewers see nothing in the slot.
  const hasMember = computed(() => !!order.value.clubMember);
  const visible = computed(() =>
    hasMember.value
      ? showPointsTile.value || showBalanceTile.value
      : clubAttachAvailable.value && canAct.value
  );
</script>

<template>
  <div
    v-if="visible"
    ref="rootRef"
    class="v2-club-member-panel"
    :class="{ 'is-row': rowLayout }"
  >
    <V2ClubMemberEmptyRow
      v-if="!hasMember"
      :compact="!rowLayout"
      @identify="showAttachClubModal"
    />
    <div v-if="hasMember" class="club-panel-header">
      <div class="club-panel-title">
        <PosUiIcon name="star-circle" class="club-panel-icon" />
        <span class="club-panel-name">{{ identity.name }}</span>
        <span v-if="identity.memberNumber != null" class="club-panel-number">
          {{ $t('posV2.clubMemberNumber', { number: identity.memberNumber }) }}
        </span>
      </div>
      <PosUiBadge
        v-if="verified"
        color="success"
        variant="soft"
        size="sm"
        icon="shield-check"
        class="club-panel-badge"
      >
        {{ $t('posV2.clubIdentified', { time: countdown }) }}
      </PosUiBadge>
      <!-- Viewers who cannot act have no code to ask for. -->
      <PosUiBadge
        v-else-if="canAct"
        color="neutral"
        variant="soft"
        size="sm"
        icon="timer-off-outline"
        class="club-panel-badge"
      >
        {{ $t('posV2.clubAskCode') }}
      </PosUiBadge>
    </div>

    <div v-if="hasMember" class="club-panel-tiles">
      <V2ClubMemberPanelTile
        v-if="showPointsTile"
        tone="points"
        :label="$t('posV2.clubPoints')"
        :value="pointsValueText"
        :caption="pointsCaption"
        :action-label="$t('posV2.clubUsePoints')"
        :action-figure="pointsActionFigure"
        icon="star-circle"
        :usable="state.points.usable"
        :can-act="canAct"
        pulse
        @action="showRedeemPointsModal"
      />
      <V2ClubMemberPanelTile
        v-if="showBalanceTile"
        tone="balance"
        :label="$t('posV2.clubBalance')"
        :value="balanceValueText"
        :caption="balanceCaption"
        :action-label="$t('posV2.clubUseBalance')"
        :action-figure="balanceValueText"
        icon="wallet"
        :usable="state.balance.usable"
        :loading="balanceLoading"
        :can-act="canAct"
        @action="payWithClubBalance"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-club-member-panel {
    @apply tw-flex tw-flex-col tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-2;

    // Stacked: identity row (name … badge) over the tiles row.
    .club-panel-header {
      @apply tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-px-0.5;

      .club-panel-title {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1.5;
      }

      .club-panel-icon {
        @apply tw-flex-shrink-0 tw-text-base tw-text-orange-600;
      }

      .club-panel-name {
        @apply tw-truncate tw-text-[13px] tw-font-semibold tw-text-neutral-900;
      }

      .club-panel-number {
        @apply tw-flex-shrink-0 tw-text-xs tw-text-neutral-500;
      }

      .club-panel-badge {
        @apply tw-flex-shrink-0 tw-tabular-nums;
      }
    }

    // Tiles share one row; a lone tile takes the whole width.
    .club-panel-tiles {
      @apply tw-flex tw-gap-2;
    }

    // The link row spans the whole slot in both layouts.
    .v2-club-member-empty-row {
      @apply tw-min-w-0 tw-flex-1;
    }

    // Wide panels: identity (name over badge) left, tiles right, one row.
    &.is-row {
      @apply tw-flex-row tw-items-center tw-gap-3;

      .club-panel-header {
        @apply tw-flex-[1_1_0] tw-flex-col tw-items-start tw-gap-0.5;

        .club-panel-title {
          @apply tw-w-full tw-flex-none;
        }
      }

      .club-panel-tiles {
        @apply tw-max-w-[480px] tw-flex-[2_1_0];
      }
    }
  }
</style>
